/**
 * MultiSelectFilter — filtre à sélection multiple avec search et badges.
 * Port BE-TAYE.
 */
import * as React from "react";
import { Check, ChevronsUpDown, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function MultiSelectFilter({
  values = [], onValuesChange, options = [],
  placeholder = "Sélectionner...", emptyText = "Aucun résultat.",
  allLabel = "Tous", className, "data-testid": dataTestId, ...props
}) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  const normalizedOptions = React.useMemo(() => {
    if (!options || !Array.isArray(options)) return [];
    return options
      .filter((o) => o !== null && o !== undefined)
      .map((o) => typeof o === "string" ? { value: o, label: o } : { value: o.value || "", label: o.label || o.value || "" })
      .filter((o) => o.value);
  }, [options]);

  const filteredOptions = React.useMemo(() => {
    if (!searchValue) return normalizedOptions;
    const s = searchValue.toLowerCase();
    return normalizedOptions.filter((o) => (o.label || "").toLowerCase().includes(s) || (o.value || "").toLowerCase().includes(s));
  }, [normalizedOptions, searchValue]);

  const getLabelForValue = (v) => normalizedOptions.find((o) => o.value === v)?.label || v || "";
  const handleToggle = (v, e) => { e.preventDefault(); e.stopPropagation();
    if (values.includes(v)) onValuesChange(values.filter((x) => x !== v)); else onValuesChange([...values, v]);
  };
  const handleSelectAll = (e) => { e.preventDefault(); e.stopPropagation(); onValuesChange([]); };
  const handleRemove = (v, e) => { e.preventDefault(); e.stopPropagation(); onValuesChange(values.filter((x) => x !== v)); };
  const handleClearAll = (e) => { e.preventDefault(); e.stopPropagation(); onValuesChange([]); };
  const isAllSelected = values.length === 0;
  const truncate = (s, n = 20) => (s.length <= n ? s : s.substring(0, n) + "...");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} data-testid={dataTestId}
          className={cn("w-full justify-between font-normal overflow-hidden h-auto min-h-[40px]",
            isAllSelected && "text-slate-500", className)} {...props}>
          <div className="flex flex-wrap gap-1 flex-1 text-left">
            {values.length === 0 ? <span>{allLabel}</span>
              : values.length <= 2 ? values.map((v) => (
                <Badge key={v} variant="secondary" className="text-xs font-normal px-1.5 py-0.5 max-w-[150px] truncate"
                  title={getLabelForValue(v)}>
                  {truncate(getLabelForValue(v))}
                  <span role="button" tabIndex={0} className="ml-1 hover:text-destructive cursor-pointer"
                    onClick={(e) => handleRemove(v, e)}
                    onKeyDown={(e) => e.key === "Enter" && handleRemove(v, e)}>
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              )) : (
                <Badge variant="secondary" className="text-xs font-normal">
                  {values.length} sélectionnés
                  <span role="button" tabIndex={0} className="ml-1 hover:text-destructive cursor-pointer"
                    onClick={handleClearAll} onKeyDown={(e) => e.key === "Enter" && handleClearAll(e)}>
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] min-w-[280px] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="flex flex-col">
          <div className="flex items-center border-b px-3 py-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input id="multi-select-search" name="multi-select-search" type="text" placeholder="Rechercher..." value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-slate-500" />
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            <div className="p-1">
              <div role="button" tabIndex={0} onClick={handleSelectAll}
                onKeyDown={(e) => e.key === "Enter" && handleSelectAll(e)}
                className={cn("relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                  isAllSelected && "bg-accent")}>
                <Check className={cn("mr-2 h-4 w-4", isAllSelected ? "opacity-100" : "opacity-0")} />
                {allLabel}
              </div>
              {filteredOptions.length > 0 && <div className="-mx-1 my-1 h-px bg-border" />}
              {filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-slate-500">{emptyText}</div>
              ) : (
                <>
                  <div className="px-2 py-1.5 text-xs font-medium text-slate-500">
                    {filteredOptions.length} option{filteredOptions.length > 1 ? "s" : ""}
                  </div>
                  {filteredOptions.map((o) => {
                    const sel = values.includes(o.value);
                    return (
                      <div key={o.value} role="button" tabIndex={0}
                        onClick={(e) => handleToggle(o.value, e)}
                        onKeyDown={(e) => e.key === "Enter" && handleToggle(o.value, e)}
                        className={cn("relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                          sel && "bg-accent/50")} title={o.label}>
                        <div className={cn("mr-2 h-4 w-4 border rounded flex items-center justify-center shrink-0",
                          sel ? "bg-primary border-primary" : "border-slate-300")}>
                          {sel && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <span className="truncate">{o.label}</span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
